import { useCallback } from 'react'

import { EditorView } from '@tiptap/pm/view'

import { useImageUploader } from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../../i18n/index.js'
import { useUploadQueue } from '../../Providers/UploadQueueProvider.js'
import { handleUploadFileError } from '../../utils/error.js'
import { FileWithID } from '../@types/index.js'
import { ImageAttributes } from '../extensions/image/index.js'
import { reconcileImage } from '../utils/image.js'

export const useAddImageToEditor = () => {
  const { addToUploadQueue, removeFromUploadQueue } = useUploadQueue()
  const { uploadImage } = useImageUploader()
  const intl = useI18n()

  const addImage = useCallback(
    async (
      view: EditorView,
      file: FileWithID,
      attrs?: Partial<ImageAttributes>,
    ) => {
      const fileSrc = URL.createObjectURL(file)

      const nodeType = view.state.schema.nodes['async-image']
      const node = nodeType.create({
        ...attrs,
        id: file.id,
        src: fileSrc,
        loading: true,
        uploadId: file.id,
      })
      const { head } = view.state.selection

      const uploadQueueItem = { id: file.id }
      addToUploadQueue(uploadQueueItem)

      try {
        /**
         * @tag Vite migration
         * Transaction does not inherit type from Transform, its a type issue on Tiptap side
         */
        const transaction = view.state.tr.insert(head, node)
        view.dispatch(transaction)

        const {
          urls: { full: url },
          id,
        } = await uploadImage(file)

        await reconcileImage(file.id, view, nodeType, {
          id,
          src: url,
        })
      } catch (error) {
        handleUploadFileError(intl)(error)
      } finally {
        removeFromUploadQueue(uploadQueueItem)
      }
    },
    [addToUploadQueue, uploadImage, intl, removeFromUploadQueue],
  )

  return {
    addImage,
  }
}
